// animSync.js — Network synchronization for animations
// Handles late-joining clients and periodic drift correction
//
// Architecture:
// - Server stores animation state (startedAt, duration, position)
// - Late joiners receive snapshot and calculate current position
// - One client becomes "leader" and broadcasts positions periodically
// - Followers compare and correct drift if > threshold
//
// Log prefix: [SYNC] for easy filtering

import { updateHitCircleColor, saveOscState } from "./animOverlays.js";
import { serverNow } from "../system/serverClock.js";
import { settleSpeedOf, loopOf } from "./cueAnimLoop.js";
// animShared imports this module too: the cycle is fine, both use the other only at call time
import { applyRemoteArmSync } from "./animShared.js";

// ============================================================================
// CONFIGURATION
// ============================================================================
const SYNC_INTERVAL_MS = 3000;   // 3 seconds between position broadcasts
const DRIFT_THRESHOLD = 0.05;    // 5% drift triggers correction (unstamped positions)
// Stamped positions (server clock) are compared in seconds of motion at 1×:
// a follower further out than JUMP_SEC jumps, one within it is slewed back
// over a while (cueAnimLoop nudge), and under SLEW_MIN_SEC is left alone.
const JUMP_SEC = 1;
const SLEW_MIN_SEC = 0.01;
// The leader looks this often for a speed change or a seek to announce at once
const CHANGE_CHECK_MS = 250;

// ============================================================================
// STATE
// ============================================================================
let _isLeader = false;
let _syncInterval = null;
let _lastBroadcastReceived = 0;  // Timestamp of last received broadcast
let _failoverCheckInterval = null;
// A leader heartbeats every SYNC_INTERVAL_MS (anim_pos_sync, even with nothing
// running). Missing two of them means the leader is gone — the normal crash
// path is faster: the server broadcasts leader_step_down the moment the
// leader's socket closes, and this timeout only covers a wedged client.
const FAILOVER_TIMEOUT_MS = 8000;
const FAILOVER_CHECK_MS = 2000;

// ============================================================================
// LEADER ELECTION
// ============================================================================
function becomeLeader() {
    if (_isLeader) return;
    _isLeader = true;
    console.log("[SYNC] This client is now LEADER");

    // Stop failover checking - we're the leader now
    if (_failoverCheckInterval) {
        clearInterval(_failoverCheckInterval);
        _failoverCheckInterval = null;
    }

    // Announce leadership to server (so late-joiners know there's a leader)
    const sock = window.socket;
    if (sock && sock.readyState === WebSocket.OPEN) {
        sock.send(JSON.stringify({ type: "leader_announce" }));
    }

    // Every SYNC_INTERVAL_MS, and as soon as a speed changes or a motion is
    // seeked, so followers hear about it within a frame or two
    let lastSent = 0;
    _syncInterval = setInterval(() => {
        const now = performance.now();
        if (now - lastSent >= SYNC_INTERVAL_MS || changedSinceBroadcast()) {
            lastSent = now;
            broadcastPositions();
        }
    }, CHANGE_CHECK_MS);
}

const _sentSpeed = new Map();   // uid -> speedMultiplier as last broadcast
let _seekedSinceBroadcast = false;

function changedSinceBroadcast() {
    if (_seekedSinceBroadcast) return true;
    for (const [uid, { cfg }] of Object.entries(window.oscillaAnimRegistry || {})) {
        if (cfg?._armState === "running" && cfg.speedMultiplier != null
            && _sentSpeed.has(uid) && _sentSpeed.get(uid) !== cfg.speedMultiplier) return true;
    }
    return false;
}

function isLeader() {
    return _isLeader;
}

// A hidden page does not step down: the background clock
// (system/backgroundClock.js) keeps animations and OSC running while
// hidden, so a leader behind other windows stays leader — the OSC laptop
// keeps feeding SuperCollider with the browser minimised.

// Start monitoring for leader failover
function startFailoverMonitoring() {
    if (_failoverCheckInterval) return;  // Already monitoring

    _failoverCheckInterval = setInterval(() => {
        if (_isLeader) return;  // We're the leader, no need to check
        // Any connected client is eligible: the leader is also the OSC source
        // (server.js oscSourceAllows), and a score of lanes, notation and
        // metros has no registry animation at all.
        const now = Date.now();
        const timeSinceLastBroadcast = now - _lastBroadcastReceived;

        if (_lastBroadcastReceived > 0 && timeSinceLastBroadcast > FAILOVER_TIMEOUT_MS) {
            becomeLeader();
        }
    }, FAILOVER_CHECK_MS);
}

// ============================================================================
// POSITION BROADCAST (Leader only)
// ============================================================================
function broadcastPositions() {
    const sock = window.socket;
    if (!sock || sock.readyState !== WebSocket.OPEN) return;

    const registry = window.oscillaAnimRegistry;
    if (!registry) return;

    const positions = {};
    for (const [uid, entry] of Object.entries(registry)) {
        const { cfg } = entry;
        // Skip if sync is explicitly disabled (sync:0 or sync:false)
        if (cfg?.sync === false || cfg?.sync === 0) continue;
        // trig:touch is synced too: local dragging is handled by the fader
        // itself broadcasting its position.

        if (cfg?._armState === "running") {
            const t = cfg._currentT;

            if (t != null) {
                const posData = {
                    t: t,
                    timestamp: Date.now()
                };
                // Include raw phase for setPhase (differs from t when start/end != 0/1)
                if (cfg._phase != null) {
                    posData.phase = cfg._phase;
                }
                // Include direction for alternate mode animations
                if (cfg._alternateDir != null) {
                    posData.dir = cfg._alternateDir;
                }
                // Include speed so followers can match (essential for staying in sync)
                if (cfg.speedMultiplier != null) {
                    posData.speed = cfg.speedMultiplier;
                    _sentSpeed.set(uid, cfg.speedMultiplier);
                }
                if (cfg._held) posData.held = true;      // grabbed (o2p scrub:1): not moving
                // Stamped on the server clock, with the loop's length, so a
                // follower can carry the phase on to its own now
                const loop = loopOf(entry);
                const at = serverNow();
                if (at != null && typeof loop?.getDur === "function" && typeof loop.getPhase === "function") {
                    posData.at = at;
                    posData.dur = loop.getDur();
                    posData.phase = loop.phaseNow?.() ?? loop.getPhase();
                }
                // Include oscEnabled and oscColor for elements with oscToggle
                if (cfg.oscToggle) {
                    posData.oscEnabled = cfg._oscEnabled ?? false;
                    // Get oscColor from hitRec if available
                    const hitRec = window._oscillaHitLabels?.find(r => r.uid === uid);
                    if (hitRec?.oscColor) {
                        posData.oscColor = hitRec.oscColor;
                    }
                }
                positions[uid] = posData;
            }
        }
    }

    _seekedSinceBroadcast = false;

    // Always send — an empty one is the leader's heartbeat, which followers'
    // failover monitor listens for (handlePositionSync stamps the receipt).
    sock.send(JSON.stringify({
        type: "anim_pos_sync",
        positions
    }));
}

// ============================================================================
// DRIFT CORRECTION (Followers only)
// ============================================================================
function handlePositionSync(positions) {
    // Record that we received a broadcast (for failover detection)
    _lastBroadcastReceived = Date.now();
    if (_isLeader) return;

    const registry = window.oscillaAnimRegistry;
    if (!registry) return;

    for (const [uid, posData] of Object.entries(positions)) {
        const entry = registry[uid];
        if (!entry) continue;

        const { cfg } = entry;
        // Skip if sync is explicitly disabled
        if (cfg?.sync === false || cfg?.sync === 0) {
            continue;
        }
        // Note: touch-triggered faders ARE synced from leader - don't skip them here
        // Only skip on broadcast side if actively being dragged locally
        if (!cfg || cfg._armState !== "running") {
            continue;
        }

        // Being dragged here (o2p scrub:1): the finger is the authority
        if (cfg._scrubbing) continue;

        // This client changed the speed or seeked after the leader measured:
        // its own state is the newer one, and the leader will say so next time
        if (posData.at != null && cfg._syncChangedAt != null && cfg._syncChangedAt > posData.at) {
            continue;
        }

        // Sync speed first - if speeds differ, position correction is useless
        if (posData.speed != null && cfg.speedMultiplier !== posData.speed) {
            settleSpeedOf(cfg);
            cfg.speedMultiplier = posData.speed;
        }

        // Handle drift correction for all animation types
        if (posData.at != null && posData.dur > 0 && posData.phase != null) {
            correctStampedDrift(entry, posData);
        } else if (posData.t != null) {
            correctO2PDrift(uid, cfg, posData.t, posData.dir, posData.phase);
        }

        // Sync OSC toggle state (only for oscToggle elements)
        if (cfg.oscToggle && posData.oscEnabled !== undefined) {
            if (cfg._oscEnabled !== posData.oscEnabled || posData.oscColor) {
                cfg._oscEnabled = posData.oscEnabled;
                const hitRec = window._oscillaHitLabels?.find(r => r.uid === uid);
                if (hitRec) {
                    hitRec.oscMode = posData.oscEnabled;
                    if (posData.oscColor) hitRec.oscColor = posData.oscColor;
                    updateHitCircleColor(hitRec);
                }
            }
        }
    }
}

/**
 * Where the leader's phase is now: its measurement carried forward by the
 * time since, at its speed. Loops wrap; an alternating loop stays on its leg.
 */
function leaderPhaseNow(posData) {
    const elapsed = Math.max(0, (serverNow() - posData.at) / 1000);
    const p = posData.phase + (posData.held ? 0 : elapsed * (posData.speed ?? 1) / posData.dur);
    if (posData.dir != null) return Math.max(0, Math.min(1, p));
    return ((p % 1) + 1) % 1;
}

function correctStampedDrift(entry, posData) {
    const { cfg } = entry;
    const loop = loopOf(entry);
    if (typeof loop?.getPhase !== "function" || typeof loop.setPhase !== "function") return;
    const target = leaderPhaseNow(posData);
    let diff = target - (loop.phaseNow?.() ?? loop.getPhase());
    if (posData.dir == null) diff -= Math.round(diff);     // the short way round
    const lagSec = Math.abs(diff) * posData.dur;
    const turned = posData.dir != null && posData.dir !== cfg._alternateDir;
    if (lagSec > JUMP_SEC || turned) {
        if (posData.dir != null && typeof loop.setDirection === "function") {
            loop.setDirection(posData.dir);
            cfg._alternateDir = posData.dir;
        }
        loop.setPhase(target);
    } else if (lagSec > SLEW_MIN_SEC && typeof loop.nudge === "function") {
        // back in step over twice the lag (at most 1.5× speed), never under 0.3 s
        loop.nudge(diff, Math.max(0.3, 2 * lagSec));
    }
}

/**
 * ui(uid:…, phase:t) — put a running motion at path position t on every
 * client. Sent with the server time it happened; a client hearing it later
 * carries it on by the time since, at the motion's speed.
 */
export function seekAnimation(uid, t, { at = null, remote = false, hold = null, quiet = false } = {}) {
    const entry = window.oscillaAnimRegistry?.[uid];
    const cfg = entry?.cfg;
    const loop = loopOf(entry);
    if (!cfg || !Number.isFinite(t) || typeof loop?.setPhase !== "function") {
        console.warn(`[ui] phase: — no running motion with uid ${uid}`);
        return;
    }
    // hold: o2p scrub:1 — held (speed 0) while grabbed, let go with false
    if (hold != null) {
        settleSpeedOf(cfg);
        cfg._held = !!hold;
    }
    if (typeof cfg._seekT === "function") cfg._seekT(t);
    else loop.setPhase(t);
    const now = serverNow();
    if (remote && at != null && now != null && !cfg._held && typeof loop.getDur === "function") {
        const p = loop.getPhase() + (now - at) / 1000 * (cfg.speedMultiplier ?? 1) / loop.getDur();
        loop.setPhase(((p % 1) + 1) % 1);
    }
    cfg._syncChangedAt = now;
    _seekedSinceBroadcast = true;
    if (remote || quiet) return;
    const sock = window.socket;
    if (sock?.readyState === WebSocket.OPEN) {
        const msg = { type: "anim_seek", uid, t, at: now };
        if (hold != null) msg.hold = !!hold;
        sock.send(JSON.stringify(msg));
    }
}

// A client that joins hears the leader's last stamped positions. Motions
// start as the score loads, so each is put in place once it runs (the
// leader's next broadcast would do it too, but up to SYNC_INTERVAL_MS late).
let _phaseSnapshotTimer = null;
window.addEventListener("oscilla:anim-phase-snapshot", (e) => {
    const pending = { ...(e.detail?.positions || {}) };
    const project = e.detail?.project || null;
    clearInterval(_phaseSnapshotTimer);
    const until = performance.now() + 15000;
    _phaseSnapshotTimer = setInterval(() => {
        if (serverNow() == null) return;            // the clock is not set yet
        if (!snapshotIsOurs(project)) return;       // another score's positions
        for (const [uid, posData] of Object.entries(pending)) {
            const entry = window.oscillaAnimRegistry?.[uid];
            if (entry?.cfg?._armState !== "running" || typeof loopOf(entry)?.getPhase !== "function") continue;
            correctStampedDrift(entry, posData);
            delete pending[uid];
        }
        if (!Object.keys(pending).length || performance.now() > until) clearInterval(_phaseSnapshotTimer);
    }, 200);
});

window.addEventListener("oscilla:anim-seek", (e) => {
    const { uid, t, at, hold } = e.detail || {};
    if (uid) seekAnimation(uid, Number(t), { at, remote: true, hold: hold ?? null });
});

function correctO2PDrift(uid, cfg, remoteT, remoteDir, remotePhase) {
    const localT = cfg._currentT;
    if (localT == null) return;

    // Calculate drift
    const drift = remoteT - localT;
    const absDrift = Math.abs(drift);

    // Handle wrap-around for looping animations
    const wrapDrift = Math.min(absDrift, 1 - absDrift);

    if (wrapDrift > DRIFT_THRESHOLD) {

        const entry = window.oscillaAnimRegistry?.[uid];
        const el = entry?.el;

        // Try to use setPhase directly on the animation loop (avoids restart issues)
        // Different animation types store the loop in different places:
        // - o2p: el._o2pAnim
        // - rotate: el._oscillaRotateAnim
        // - scale: el._oscillaScaleAnim
        // - color: el._oscillaColorAnim
        // - all continuous modes also set cfg._anim
        const animLoop = el?._o2pAnim || el?._oscillaRotateAnim || el?._oscillaScaleAnim || el?._oscillaColorAnim || cfg._anim;

        if (animLoop && typeof animLoop.setPhase === 'function') {
            // Direction first: setPhase maps t onto a leg using the current
            // direction, and a stale one (mode:alternate, still heading home
            // while the leader has already turned) lands the follower on the
            // home leg — it then "arrives" and emits a spurious waypoint -1.
            if (remoteDir != null && typeof animLoop.setDirection === 'function') {
                animLoop.setDirection(remoteDir);
                cfg._alternateDir = remoteDir;
            }
            // Use raw phase if available (required when start/end != 0/1)
            // Fall back to remoteT for animations where phase === globalT
            const phaseToSet = remotePhase ?? remoteT;
            animLoop.setPhase(phaseToSet);
            cfg._currentT = remoteT;
            if (remotePhase != null) cfg._phase = remotePhase;
        } else if (!animLoop) {
            // Not running here: start it at the leader's position (the drag
            // resume mechanism). Something that runs but cannot seek -- a
            // rotate or scale sequence (its slot holds a tween or a hold's
            // timeout), a follow -- is left alone: a restart would put a
            // sequence back to its first value on every correction.
            cfg._dragResumeT = remoteT;
            cfg._remoteSync = true;

            // Restart the animation
            if (typeof cfg._start === "function") {
                cfg._start();
            }

            cfg._remoteSync = false;
        }
    }
}

// ============================================================================
// LATE-JOIN SNAPSHOT HANDLING
// ============================================================================

// Store pending snapshot for animations that haven't registered yet
window._pendingAnimSnapshot = null;

// A snapshot names the score its animations belong to, and is applied only
// to that score: it arrives on connect, often before the project is open,
// and a uid such as r1 can be in any score. Checked when it is applied, so
// a snapshot kept on the start page reaches the room's project if that is
// the one opened. A snapshot without a project (an older server) is trusted.
export function snapshotIsOurs(project, current = window.currentProject) {
    return !project || !current || project === current;
}

function handleStateSnapshot(animStateMap, project = null) {
    if (!animStateMap || typeof animStateMap !== "object") return;

    // Store a copy for animations that register later
    const pendingMap = { ...animStateMap };
    const now = Date.now();

    // Apply immediately to any animations already registered
    if (snapshotIsOurs(project)) {
        for (const [uid, stateData] of Object.entries(animStateMap)) {
            if (window.oscillaAnimRegistry?.[uid]) {
                applySnapshotState(uid, stateData, now);
                delete pendingMap[uid];
            }
        }
    }

    // Store remaining for later application
    if (Object.keys(pendingMap).length > 0) {
        window._pendingAnimSnapshot = { map: pendingMap, project, receivedAt: now };
    } else {
        window._pendingAnimSnapshot = null;
    }
}

// Called when a new animation registers - check for pending snapshot state
export function _applyPendingSnapshotForUid(uid) {
    const pending = window._pendingAnimSnapshot;
    if (!pending?.map?.[uid]) return;
    if (!snapshotIsOurs(pending.project)) return;

    applySnapshotState(uid, pending.map[uid], Date.now());

    delete pending.map[uid];
    if (Object.keys(pending.map).length === 0) {
        window._pendingAnimSnapshot = null;
    }
}

function applySnapshotState(uid, stateData, now) {
    const { state, resumeT, startedAt, dur, lastKnownT, oscEnabled, oscColor } = stateData;

    if (state === "running") {
        let calculatedT = resumeT ?? 0;

        // Calculate current position from elapsed time
        if (startedAt && dur && dur > 0) {
            const elapsedSec = (now - startedAt) / 1000;
            const durationSec = dur;
            // For looping animations, wrap around
            calculatedT = (elapsedSec / durationSec) % 1;
            // Add the starting offset
            if (resumeT) {
                calculatedT = (calculatedT + resumeT) % 1;
            }
        } else if (lastKnownT != null) {
            calculatedT = lastKnownT;
        }

        applyRemoteArmSync(uid, "running", { resumeT: calculatedT });

        // Apply OSC toggle state after animation starts
        if (oscEnabled !== undefined) {
            setTimeout(() => applyOscToggleState(uid, oscEnabled, oscColor), 200);
        }

    } else if (state === "paused") {
        const pauseT = lastKnownT ?? resumeT ?? 0;
        // Start then immediately pause at the right position
        applyRemoteArmSync(uid, "running", { resumeT: pauseT });
        setTimeout(() => {
            applyRemoteArmSync(uid, "paused", { resumeT: pauseT });
        }, 100);

        // Apply OSC toggle state after animation starts
        if (oscEnabled !== undefined) {
            setTimeout(() => applyOscToggleState(uid, oscEnabled, oscColor), 200);
        }
    }
}

function applyOscToggleState(uid, oscEnabled, clientColor) {
    const registry = window.oscillaAnimRegistry;
    if (!registry) return;

    const entry = registry[uid];
    if (!entry) return;

    const { cfg } = entry;
    if (!cfg?.oscToggle) return;

    cfg._oscEnabled = oscEnabled;
    const hitRec = window._oscillaHitLabels?.find(r => r.uid === uid);
    if (hitRec) {
        hitRec.oscMode = oscEnabled;
        hitRec.oscColor = clientColor;  // Store who toggled it
        hitRec.toggledByLocal = false;  // Remote toggle, not local
        // Persist to localStorage so it survives page reload
        saveOscState(uid, oscEnabled, clientColor);
        updateHitCircleColor(hitRec);
    }
}

// ============================================================================
// EVENT LISTENERS
// ============================================================================
window.addEventListener("oscilla:anim-pos-sync", (e) => {
    const { positions } = e.detail || {};
    if (positions) {
        handlePositionSync(positions);
    }
});

window.addEventListener("oscilla:anim-state-snapshot", (e) => {
    handleStateSnapshot(e.detail?.animStateMap, e.detail?.project);
});

window.addEventListener("oscilla:osc-toggle-sync", (e) => {
    const { uid, oscEnabled, clientColor } = e.detail || {};
    if (uid !== undefined && oscEnabled !== undefined) {
        applyOscToggleState(uid, oscEnabled, clientColor);
    }
});

// Leader stepped down - volunteer to become leader (visible clients first)
window.addEventListener("oscilla:leader-step-down", () => {
    if (_isLeader) return;  // We are the one stepping down
    // Every client is eligible. Random delay breaks the tie; hidden pages
    // wait longer so a visible one wins when there is one. The server
    // answers the winner's announce with has_leader to everyone else, so a
    // second volunteer steps back down.
    _serverHasLeader = false;
    _lastBroadcastReceived = Date.now();      // don't also fire the timeout path
    const delay = 100 + Math.random() * 400 + (document.hidden ? 1000 : 0);
    setTimeout(() => {
        if (!_isLeader && !_serverHasLeader) {
            becomeLeader();
        }
    }, delay);
});

// ============================================================================
// AUTO-LEADER ELECTION
// If no one is leader shortly after page load, volunteer
// ============================================================================
let _receivedSnapshot = false;
let _serverHasLeader = false;

window.addEventListener("oscilla:anim-state-snapshot", () => {
    _receivedSnapshot = true;  // Mark that we're a late joiner
    _lastBroadcastReceived = Date.now();  // Pretend we just got a broadcast
    startFailoverMonitoring();  // Start checking for leader failover
});

// Server tells us there's already a leader
window.addEventListener("oscilla:has-leader", () => {
    _serverHasLeader = true;
    _lastBroadcastReceived = Date.now();

    // If we thought we were leader, step down
    if (_isLeader) {
        console.log("[SYNC] Server says another client is leader - stepping down");
        _isLeader = false;
        if (_syncInterval) {
            clearInterval(_syncInterval);
            _syncInterval = null;
        }
    }

    startFailoverMonitoring();
});

// After page load, with no snapshot received (we are the first client),
// become leader
window.addEventListener("load", () => {
    // Random delay (2-4 seconds) to prevent race conditions when multiple clients load simultaneously
    const delay = 2000 + Math.random() * 2000;
    setTimeout(() => {
        if (_isLeader) return;  // Already leader

        // If we received a snapshot, a broadcast, or server said there's a leader, don't self-elect
        if (_receivedSnapshot || _serverHasLeader || _lastBroadcastReceived > 0) {
            startFailoverMonitoring();
            return;
        }

        // First client in: lead. Nothing needs to be running — the leader
        // is also the ensemble's OSC source.
        becomeLeader();
    }, delay);
});

// ============================================================================
// EXPORTS
// ============================================================================
export {
    becomeLeader,
    isLeader,
};
